import {
  Card,
  CardBody,
  CardHeader,
  Badge,
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { OfferedOffer } from '@texas/api/endpoints/offerApi';
import { Icons } from '@texas/components/shared/Icons';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { TFunction } from 'i18next';

interface Props {
  offeredOffers: OfferedOffer[];
  loading: boolean;
  onOpenOfferedOffer: (id: number) => void;
  t: TFunction;
}

export function SentOfferedOffersCard({
  offeredOffers,
  loading,
  onOpenOfferedOffer,
  t,
}: Props) {
  return (
    <Card variant="dark" mb={6}>
      <CardHeader>
        <Flex align="center" justify="space-between">
          <Heading size="sm">{t('offer.sentOfferedOffers')}</Heading>
          {loading && <Spinner size="xs" />}
        </Flex>
      </CardHeader>
      <CardBody>
        {offeredOffers.length === 0 ? (
          <Text fontSize="sm" color="gray.400">
            {t('general.noResult')}
          </Text>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('offer.offerNumber')}</Th>
                  <Th>{t('general.contact')}</Th>
                  <Th>{t('offer.basedOn')}</Th>
                  <Th>{t('offer.sent')}</Th>
                  <Th>{t('offer.expires')}</Th>
                  <Th>{t('offer.cancelled')}</Th>
                  <Th>{t('offer.cancelledBy')}</Th>
                  <Th>{t('offer.prices')}</Th>
                  <Th>{t('general.branch')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {offeredOffers.map((offeredOffer) => {
                  const isCancelled = !!offeredOffer.cancelledAt;
                  const isAccepted =
                    offeredOffer.tierPriceCount > 0 &&
                    offeredOffer.acceptedTierPriceCount ===
                      offeredOffer.tierPriceCount;

                  return (
                    <Tr
                      key={offeredOffer.id}
                      cursor="pointer"
                      textDecoration={isCancelled ? 'line-through' : undefined}
                      _hover={{ bg: 'whiteAlpha.50' }}
                      onClick={() => onOpenOfferedOffer(offeredOffer.id)}
                    >
                      <Td>
                        <Text fontWeight="medium">
                          {offeredOffer.offerNumber}
                        </Text>
                        {isAccepted && (
                          <Badge colorScheme="green" fontSize="2xs">
                            {t('general.accepted')}
                          </Badge>
                        )}
                        <Text fontSize="xs" color="gray.400">
                          {offeredOffer.ownerName}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{offeredOffer.contactName}</Text>
                        <Text fontSize="xs" color="gray.400">
                          {offeredOffer.contactEmail}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {offeredOffer.basedOnOfferedOfferNumber ?? ''}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {formatDate(Locale.En, offeredOffer.sentOn)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {formatDate(Locale.En, offeredOffer.expiresOn)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {offeredOffer.cancelledAt
                            ? formatDate(Locale.En, offeredOffer.cancelledAt)
                            : ''}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {offeredOffer.cancelledByName ?? ''}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {offeredOffer.acceptedTierPriceCount}/
                          {offeredOffer.tierPriceCount}
                        </Text>
                      </Td>
                      <Td>
                        <Flex gap={1} wrap="wrap">
                          {offeredOffer.branches.map((branch) => {
                            const hasAcceptedPrices = branch.currencies.some(
                              (currency) =>
                                currency.tierPrices.some(
                                  (tierPrice) => tierPrice.acceptedDate,
                                ),
                            );

                            return (
                              <Tag
                                key={branch.branchId}
                                size="sm"
                                colorScheme={
                                  hasAcceptedPrices ? 'green' : 'gray'
                                }
                              >
                                {hasAcceptedPrices && (
                                  <Icons.Checkmark boxSize={3} mr={1} />
                                )}
                                {branch.branchIdentifier}
                              </Tag>
                            );
                          })}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </CardBody>
    </Card>
  );
}
